import React, {
    ChangeEvent,
    Dispatch,
    ReactNode,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { Switch } from '../../components/ui/switch'
import { InputGroup, NumericInput, TextArea } from '@blueprintjs/core'
import { HTMLSelect } from '../../components/ui/html-select'
import { Callout } from '../../components/ui/callout'
import { Layout } from '../../components/Layout'
import { FormInput } from '../../components/auth/common'
import { PlaceholderDrawer } from '../../components/tools/quick_create_variables_drawer'
import { Steps } from '../../components/tools/steps'
import { axiosPost } from '../../util/api'
import { SelectBoxes } from '../../components/SelectBoxes'
import { Classes, Popover } from '../../components/ui/popover'
import { Button } from '../../components/ui/button'

const SINGLE_ASIN_STRUCTURE = 1
const MULTI_ASIN_STRUCTURE = 2

class PendingStructure {
    sellerType: string
    sellerTypeValue: number
    asinsWithTitles: AsinWithTitle
    portfolio_id: string | undefined
    portfolio_name: string | undefined
    platform: string
    platformShort: string
    title: string = ''
    campaigns: PendingCampaign[]
    default_daily_budget: number | string
    default_bid: number | string
    campaign_name_template: string
    ad_group_name_template: string
    dummy_asin: string = ''
    asin: string = ''

    constructor(
        platform: string,
        platformShort: string,
        defaultDailyBudget: number,
        defaultBid: number,
        campaigns: PendingCampaign[],
    ) {
        this.sellerType = 'vendor'
        this.sellerTypeValue = 1
        this.asinsWithTitles = {}
        this.platform = platform
        this.platformShort = platformShort
        this.campaigns = campaigns
        this.default_daily_budget = defaultDailyBudget
        this.default_bid = defaultBid
        this.campaign_name_template =
            '{{asin}} - {{platform-short}}{{type-short}} {{nickname}} - {{title}}'
        this.ad_group_name_template =
            '{{asin}} - {{platform-short}}{{type-short}} {{nickname}} {{targeting}} - {{title}}'
    }
}

class PendingCampaign {
    name?: string
    type: string = ''
    nickname: string = ''
    splitByTargeting: boolean = false
    relatedAsin?: string
    relatedTitle?: string
    adGroups: PendingAdGroup[] = []
}

class PendingAdGroup {
    name: string = ''
    type: string = ''
    targeting: string = ''
}

type AsinWithTitle = {
    [key: string]: string
}

const createDefaultCampaignsWithAsins = (
    structure: PendingStructure,
    asin: string,
    title: string,
): Array<PendingCampaign> => {
    return [
        {
            type: 'Auto',
            nickname: 'Auto',
            splitByTargeting: false,
            adGroups: [],
            relatedAsin: asin,
            relatedTitle: title,
            name: resolveName(
                structure.campaign_name_template,
                structure,
                'Auto',
                'Auto',
                '',
                asin,
                title,
            ),
        },
        {
            type: 'Manual',
            nickname: 'Testing',
            splitByTargeting: false,
            adGroups: [],
            relatedAsin: asin,
            relatedTitle: title,
            name: resolveName(
                structure.campaign_name_template,
                structure,
                'Manual',
                'Testing',
                '',
                asin,
                title,
            ),
        },
        {
            type: 'Manual',
            nickname: 'Performance',
            splitByTargeting: false,
            adGroups: [],
            relatedAsin: asin,
            relatedTitle: title,
            name: resolveName(
                structure.campaign_name_template,
                structure,
                'Manual',
                'Performance',
                '',
                asin,
                title,
            ),
        },
    ]
}

export const QuickCreate = ({ nested = false }): JSX.Element => {
    const [structure, setStructure] = useState(
        new PendingStructure('Sponsored Products', 'SP', 50.0, 0.75, []),
    )
    const [createType, setCreateType] = useState<number>(SINGLE_ASIN_STRUCTURE)
    const [step, setStep] = useState(1)
    const [multiAsinRawInput, setMultiAsinRawInput] = useState<string>('')
    const [identificationNumberType, setIdentificationNumberType] = useState<string>('') // ASIN or SKU

    useEffect(() => {
        const _identificationNumberType: string = structure?.sellerType == 'vendor' ? 'ASIN' : 'SKU'
        setIdentificationNumberType(_identificationNumberType)
        setStructure((s) => {
            return {
                ...s,
                campaign_name_template: `{{${_identificationNumberType.toLocaleLowerCase()}}} - {{platform-short}}{{type-short}} {{nickname}} - {{title}}`,
                ad_group_name_template: `{{${_identificationNumberType.toLocaleLowerCase()}}} - {{platform-short}}{{type-short}} {{nickname}} {{targeting}} - {{title}}`,
            }
        })
    }, [structure?.sellerType])

    const setDummyAsin = (v: string) => {
        setStructure((p) => ({
            ...p,
            dummy_asin: v,
        }))
    }

    const setAsinWithTitle = (v: string) => {
        setMultiAsinRawInput(v)
        const textArray = v.split('\n')
        let data: Array<AsinWithTitle> = []
        data = textArray
            .filter((item) => item?.split(',').length > 1)
            .map((item) => {
                const temp = item?.split(',')

                // Strip out whitespace
                const key = temp[0].replace(/\s/g, '')
                const value = temp[1].replace(/\s/g, '')

                return {
                    [key]: value,
                }
            })

        data = data.filter((item) => {
            if (item == null) {
                return false
            }

            return !(item[Object.keys(item)[0]] == null || item[Object.keys(item)[0]] == '')
        })

        let finalData: AsinWithTitle = {}
        data.forEach((item) => {
            finalData = {
                ...finalData,
                [Object.keys(item)[0]]: item[Object.keys(item)[0]],
            }
        })

        setStructure((p) => {
            return {
                ...p,
                asinsWithTitles: finalData,
            }
        })
    }

    // For single Asin/Sku
    const setAsin = (asin: string) => {
        setStructure((s) => ({
            ...s,
            asinsWithTitles: {
                [asin]: '',
            },
        }))
    }

    // For single Asin/Sku
    const setTitle = (title: string) => {
        const keys = Object.keys(structure.asinsWithTitles)
        if (keys.length <= 0) {
            return
        }

        setStructure((s) => ({
            ...s,
            asinsWithTitles: {
                [keys[0]]: title,
            },
        }))
    }

    const back = () => setStep((p) => p - 1)
    const next = () => {
        if (step === 5) {
            const payload = {
                sellerType: structure.sellerType,
                asinsWithTitles: structure.asinsWithTitles,
                platform: 1, // @todo
                default_daily_budget: parseFloat('' + structure.default_daily_budget),
                default_bid: parseFloat('' + structure.default_bid),
                campaign_name_template: structure.campaign_name_template,
                ad_group_name_template: structure.ad_group_name_template,
                portfolio_id: structure.portfolio_id == '-1' ? undefined : structure.portfolio_id,
                dummy_asin: structure.sellerType == 'seller' ? structure.dummy_asin : '',
            }

            axiosPost('api/v2/template', payload, true, { responseType: 'blob' }).then((res) => {
                const url = window.URL.createObjectURL(new Blob([res.data]))
                const link = document.createElement('a')
                link.href = url
                const asins = Object.keys(structure.asinsWithTitles)
                if (asins.length == 1) {
                    link.setAttribute(
                        'download',
                        asins[0] + ` - Single ${identificationNumberType} Campaign Structure.xlsx`,
                    )
                } else {
                    link.setAttribute(
                        'download',
                        `Multi ${identificationNumberType} Campaign Structure.xlsx`,
                    )
                }
                document.body.appendChild(link)
                link.click()
                window.URL.revokeObjectURL(url)
            })

            setStep((p) => p + 1)
        } else {
            setStep((p) => p + 1)
        }
    }

    const asinLabel = useMemo(() => {
        const content = (
            <span>
                <p>
                    We'll create a temporary product targeting ad to get your campaign structure
                    setup. Enter in any ASIN that you have a listing for that can be advertised here
                    to use that as the temporary product targeting ad.
                </p>
                <p className="p-0">
                    This ASIN will not be advertised, but is just used for the initial setup!
                </p>
            </span>
        )
        return (
            <Popover
                popoverClassName={Classes.POPOVER2_CONTENT_SIZING}
                content={content}
                interactionKind="hover"
            >
                <label className="cursor-pointer border-black border-b border-dashed w-8">
                    ASIN
                </label>
            </Popover>
        )
    }, [])

    const stepVerbiage = useMemo(() => {
        if (step === 5) {
            return 'Download Bulk File'
        } else if (step === 4 && structure.portfolio_id === '-1') {
            return 'Skip'
        }

        return 'Next'
    }, [step, JSON.stringify(structure)])

    const breadcrumbs = [{ text: 'Tools' }, { href: '', text: 'Quick Create' }]

    return (
        <Layout breadcrumbs={breadcrumbs} primary="Quick Create" nested={nested}>
            <Callout className="mb-4" intent="primary" title="Beta Feature" icon="info-sign">
                <p className="max-w-3xl">
                    Quick Create is a beta feature that is still rough around the edges. We will be
                    continuing to add additional features, options, and functionality to make this
                    tool more powerful, flexible, and easier to use!
                </p>
            </Callout>
            <Steps identificationNumberType={identificationNumberType} step={step} />
            {step === 1 && <SellerType setStructure={setStructure} structure={structure} />}
            {step === 2 && (
                <>
                    <div className="mb-2 max-w-screen-md">
                        Quick Create allows you to quickly create a single{' '}
                        {identificationNumberType} campaign structure for a given{' '}
                        {identificationNumberType}.{/* , as well*/}
                        {/* as automatically creating promotion rules for it! Enter in one of your ASINs below to get started.*/}
                    </div>
                    <div className="flex">
                        <p className="mr-2">Single {identificationNumberType}</p>
                        <Switch
                            checked={createType == MULTI_ASIN_STRUCTURE}
                            label={`Multi ${identificationNumberType}s`}
                            onChange={() => {
                                setCreateType((p) =>
                                    p == SINGLE_ASIN_STRUCTURE
                                        ? MULTI_ASIN_STRUCTURE
                                        : SINGLE_ASIN_STRUCTURE,
                                )
                            }}
                        />
                    </div>
                    {createType == SINGLE_ASIN_STRUCTURE ? (
                        <div className="mb-4">
                            <label className="mb-0">{identificationNumberType}</label>
                            <InputGroup
                                className="mb-2"
                                style={{ width: '200px' }}
                                type="text"
                                placeholder={identificationNumberType}
                                value={
                                    !!structure?.asinsWithTitles
                                        ? Object.keys(structure?.asinsWithTitles)[0]
                                        : ''
                                }
                                onChange={(e) => setAsin(e.target.value)}
                            />
                            <label className="mb-0">Product Title</label>
                            <InputGroup
                                className="mb-2"
                                style={{ width: '200px' }}
                                type="text"
                                placeholder="My product title"
                                value={
                                    !!structure?.asinsWithTitles
                                        ? structure?.asinsWithTitles[
                                              Object.keys(structure?.asinsWithTitles)[0]
                                          ]
                                        : ''
                                }
                                onChange={(e) => setTitle(e.target.value)}
                            />
                            {structure.sellerType == 'seller' && (
                                <>
                                    {asinLabel}
                                    <InputGroup
                                        style={{ width: '200px' }}
                                        placeholder={'ASIN'}
                                        onChange={(e) => setDummyAsin(e.target.value)}
                                        value={structure.dummy_asin}
                                    />
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="mb-4">
                            <div>
                                <h6>{identificationNumberType}, Product Title</h6>
                                <TextArea
                                    className="mb-2"
                                    style={{ width: '200px' }}
                                    placeholder={identificationNumberType + ',Product Title'}
                                    onChange={(e) => setAsinWithTitle(e.target.value)}
                                    value={multiAsinRawInput}
                                />
                            </div>
                            {structure.sellerType == 'seller' && (
                                <>
                                    {asinLabel}
                                    <InputGroup
                                        className="mb-0"
                                        style={{ width: '200px' }}
                                        placeholder={'ASIN'}
                                        onChange={(e) => setDummyAsin(e.target.value)}
                                        value={structure.dummy_asin}
                                    />
                                </>
                            )}
                        </div>
                    )}
                </>
            )}
            {step === 3 && <CampaignNames structure={structure} setStructure={setStructure} />}
            {step === 4 && <AdGroupNames structure={structure} setStructure={setStructure} />}
            {step === 5 && <Portfolios setStructure={setStructure} structure={structure} />}
            {step === 6 && <Upload />}
            {/* {step === 6 && <Review structure={structure} identificationNumberType={identificationNumberType}/>}*/}

            <div className="mt-6">
                {step != 1 && <Button text="Back" large onClick={() => back()} className="mr-4" />}
                <Button
                    text={stepVerbiage}
                    large
                    disabled={step === 6}
                    onClick={() => next()}
                    rightIcon="arrow-right"
                    intent="primary"
                />
            </div>
        </Layout>
    )
}

const resolveName = (
    template: string,
    structure: PendingStructure,
    type: string,
    nickname: string,
    targeting = '',
    asin = '',
    title = '',
) => {
    return template
        .replace('{{nickname}}', nickname)
        .replace('{{targeting}}', targeting)
        .replace('{{type}}', type)
        .replace('{{type-short}}', type === 'Auto' ? 'A' : 'M')
        .replace('{{platform}}', structure.platform)
        .replace('{{platform-short}}', structure.platformShort)
        .replace('{{asin}}', asin == '' ? 'undefined' : asin)
        .replace('{{sku}}', asin == '' ? 'undefined' : asin)
        .replace('{{title}}', title == '' ? structure.title : title)
}

interface CampaignNamesProps {
    structure: PendingStructure
    setStructure: Dispatch<SetStateAction<PendingStructure>>
}

const CampaignNames = ({ structure, setStructure }: CampaignNamesProps) => {
    const [open, setOpen] = useState(false)

    useEffect(() => {
        for (const asin in structure.asinsWithTitles) {
            if (structure.asinsWithTitles.hasOwnProperty(asin)) {
                structure.campaigns.push(
                    ...createDefaultCampaignsWithAsins(
                        structure,
                        asin,
                        structure.asinsWithTitles[asin],
                    ),
                )
            }
        }
        setStructure({ ...structure })
    }, [structure.campaign_name_template, structure.title, structure.asin])

    return (
        <div>
            <div>Campaign Names</div>
            <div className="flex items-left">
                <div className="flex-basis-50 mr-12">
                    <label>Campaign Name Template</label>
                    <div
                        className="text-blue-500 text-sm font-normal cursor-pointer"
                        onClick={() => setOpen(true)}
                    >
                        Learn more about templates
                    </div>
                    <FormInput
                        type="text"
                        value={structure.campaign_name_template}
                        onChange={(v) =>
                            setStructure((p) => ({
                                ...p,
                                campaign_name_template: v,
                            }))
                        }
                    />

                    <label>Daily Budget</label>
                    <NumericInput
                        leftIcon={<Button minimal>{window.marketplace.currency_symbol}</Button>}
                        value={structure.default_daily_budget}
                        buttonPosition="none"
                        onValueChange={(_, v) => {
                            setStructure((p) => ({
                                ...p,
                                default_daily_budget: v,
                            }))
                        }}
                    />
                </div>
                <div className="flex-basis-50">
                    <div className="font-normal">Campaign Name Preview</div>
                    <ScrollableDiv>
                        {structure.campaigns.map((c, i) => (
                            <div key={i}>
                                <div>
                                    {c.nickname}: {c.name}
                                </div>
                            </div>
                        ))}
                    </ScrollableDiv>
                </div>
            </div>
            <PlaceholderDrawer
                open={open}
                setOpen={setOpen}
                title={structure.title}
                asin={structure.asin}
            />
        </div>
    )
}

const AdGroupNames = ({ structure, setStructure }: CampaignNamesProps) => {
    const [open, setOpen] = useState(false)

    const setSplit = (i: number, v: boolean) =>
        setStructure((p) => ({
            ...p,
            campaigns: p.campaigns.map((c, index) => {
                if (index !== i) {
                    return c
                }

                c.splitByTargeting = v

                const resolve = (targeting: string): PendingAdGroup => {
                    const name = resolveName(
                        structure.ad_group_name_template,
                        p,
                        c.type,
                        c.nickname,
                        targeting,
                        c.relatedAsin,
                        c.relatedTitle,
                    )

                    return {
                        name,
                        type: c.type,
                        targeting,
                    }
                }

                if (c.type === 'Auto') {
                    if (c.splitByTargeting) {
                        c.adGroups = [
                            resolve('Loose'),
                            resolve('Close'),
                            resolve('Complements'),
                            resolve('Substitutes'),
                        ]
                    } else {
                        c.adGroups = [resolve('All')]
                    }
                } else {
                    if (c.splitByTargeting) {
                        c.adGroups = [
                            resolve('Broad'),
                            resolve('Phrase'),
                            resolve('Exact'),
                            resolve('Products'),
                        ]
                    } else {
                        c.adGroups = [resolve('Keywords'), resolve('Products')]
                    }
                }

                return c
            }),
        }))

    useEffect(() => {
        structure.campaigns.forEach((c, i) => setSplit(i, c.splitByTargeting))
    }, [])

    useEffect(() => {
        structure.campaigns.forEach((c, i) => setSplit(i, c.splitByTargeting))
    }, [structure.ad_group_name_template])

    return (
        <div>
            <div>Campaign Names</div>
            <div className="flex items-left">
                <div className="flex-basis-50 mr-12">
                    <label>Ad Group Name Template</label>
                    <div
                        className="text-blue-500 text-sm font-normal cursor-pointer"
                        onClick={() => setOpen(true)}
                    >
                        Learn more about templates
                    </div>
                    <FormInput
                        type="text"
                        value={structure.ad_group_name_template}
                        onChange={(v) =>
                            setStructure((p) => ({
                                ...p,
                                ad_group_name_template: v,
                            }))
                        }
                    />

                    <label>Default Bid</label>
                    <NumericInput
                        leftIcon={<Button minimal>{window.marketplace.currency_symbol}</Button>}
                        value={structure.default_bid}
                        buttonPosition="none"
                        onValueChange={(_, v) => {
                            setStructure((p) => ({
                                ...p,
                                default_bid: v,
                            }))
                        }}
                    />
                </div>
                <div className="flex-basis-50">
                    <div className="font-normal">Ad Group Name Preview</div>
                    <ScrollableDiv>
                        {structure.campaigns.map((c, i) => (
                            <div key={i}>
                                <div className="mt-3">
                                    {c.nickname} campaign: {c.name}
                                </div>
                                <div className="ml-4">
                                    {c.adGroups.map((k, i) => (
                                        <div key={i}>Ad Group: {k.name}</div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </ScrollableDiv>
                </div>
            </div>
            <PlaceholderDrawer
                open={open}
                setOpen={setOpen}
                title={structure.title}
                asin={structure.asin}
            />
        </div>
    )
}

const Upload = ({}) => {
    return (
        <div className="flex flex-col items-center">
            <div className="flex-basis-50 mb-6 max-w-screen-md w-full">
                <div className="text-lg mb-2">Almost there!</div>
                <div className="mb-4">
                    Now you'll just want to upload your recently downloaded bulk file to Amazon in
                    order tocreate your single ASIN campaign structure.
                </div>
                <ol className="list-decimal ml-4">
                    <li className="text-lg mb-3">
                        <div className="font-normal mb-2">
                            Sign into your Amazon Advertising account
                        </div>
                        <a
                            className="text-sm"
                            href="https://advertising.amazon.com/cm"
                            target="_blank"
                            rel="noreferrer"
                        >
                            https://advertising.amazon.com/cm
                        </a>
                    </li>
                    <li className="text-lg mb-3">
                        <div className="font-normal mb-2">Navigate to the Bulk operations area</div>
                        <div className="text-sm mb-2">
                            On the left hand side of the Amazon Advertising platform, click the
                            Sponsored Ads button, then Bulk operations.
                        </div>
                        <img src="/bulk-upload/2.png" alt="Step 2" />
                    </li>
                    <li className="text-lg mb-3">
                        <div className="font-normal mb-2">Select the new spreadsheet format</div>
                        <img src="/bulk-upload/3.png" alt="Step 3" />
                    </li>
                    <li className="text-lg mb-3">
                        <div className="font-normal mb-2">Upload your bulk file</div>

                        <div className="text-sm mb-2">
                            Then click "Upload to process changes" Under step 3 click "Choose file"
                            and then select the bulk file that you downloaded from this email.
                        </div>
                        <img className="mb-2" src="/bulk-upload/4a.png" alt="Step 4a" />
                        <img src="/bulk-upload/4b.png" alt="Step 4b" />
                    </li>
                    <li className="text-lg mb-3">
                        <div className="font-normal mb-2">Wait for your bulk file to process</div>

                        <div className="text-sm mb-2">
                            Usually, Amazon is pretty quick in processing small bulk files uploads
                            (generally it will take them just a few minutes), but occasionally it
                            can take up to a few hours.
                        </div>
                    </li>
                </ol>
            </div>
        </div>
    )
}

const Portfolios = ({ setStructure, structure }: CampaignNamesProps) => {
    type Portfolio = {
        portfolio_id: string
        name: string
    }
    const [selected, setSelected] = useState<number>(-1)
    const [portfolioOptions, setPortfolioOptions] = useState<Array<Portfolio>>([])

    useEffect(() => {
        // load()
        if (structure.portfolio_id == null) {
            setStructure((p) => {
                return {
                    ...p,
                    portfolio_id: '-1',
                    portfolio_name: undefined,
                }
            })
        }
    }, [])

    const onChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const { value, options, selectedIndex } = e.target

        setSelected(Number(e.currentTarget.value))
        setStructure((p) => {
            return {
                ...p,
                portfolio_id: value,
                portfolio_name: options[selectedIndex]?.text,
            }
        })
    }

    return (
        <div>
            <div className="flex-basis-50 mb-6">
                <div className="mb-2">
                    Select which portfolio you'd like your newly created campaigns to belong to. If
                    not, feel free to skip this step.
                </div>
                <HTMLSelect
                    value={structure.portfolio_id}
                    onChange={onChange}
                    options={portfolioOptions.map((portfolio) => ({
                        label: portfolio.name,
                        value: portfolio.portfolio_id,
                    }))}
                />
            </div>
        </div>
    )
}

const SellerType = ({ structure, setStructure }: CampaignNamesProps) => {
    return (
        <div>
            <SelectBoxes
                options={[
                    {
                        label: 'Merch',
                        value: 1,
                    },
                    {
                        label: 'KDP',
                        value: 2,
                        disabled: true,
                        tooltip:
                            'KDP does not yet support bulk uploads at this time. API integration coming soon!',
                    },
                    {
                        label: 'Vendor Central',
                        value: 3,
                    },
                    {
                        label: 'Seller Central',
                        value: 4,
                    },
                ]}
                value={structure.sellerTypeValue}
                onChange={(val: number) => {
                    setStructure((p) => ({
                        ...p,
                        sellerType: val == 4 ? 'seller' : 'vendor',
                        sellerTypeValue: val,
                    }))
                }}
            />
        </div>
    )
}

const ScrollableDiv = ({ children }: { children: ReactNode }) => {
    return (
        <div style={{ width: 600, maxHeight: 500 }} className="overflow-y-auto">
            {children}
        </div>
    )
}
